import PropTypes from 'prop-types';
import React from 'react';

// components
import CartSummaryDiscount from './CartSummaryDiscount';
import CartSummaryDiscountDisplay from './CartSummaryDiscountDisplay';
import CartSummaryItems from './CartSummaryItems';
import { BodyMedium, BodySmall, DividerLightGreyStyled, H4 } from '@styled';

// functionality
import { formatMoney } from '@general/helpers/currency.helpers';
import useViewer from '@hooks/useViewer';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import TooltipInfo from '@atoms/TooltipInfo/TooltipInfo';

const CartSummary = ({ hideDiscountsSection = false, setCartHasErrors }) => {
    const { viewer } = useViewer();
    const { cart } = useCartFromAPI({ viewerId: viewer?.recordId });

    if (!cart) return null;

    const isCartEmpty = cart.items.length === 0;

    return (
        <div>
            <H4>Summary</H4>
            <DividerLightGreyStyled className="mb-1rem mt-1.875rem" />

            <div className="flex justify-between mb-1rem">
                <BodyMedium>Subtotal</BodyMedium>
                <BodyMedium>{formatMoney(cart._calculated.subtotalWithoutDiscounts, cart.locale)}</BodyMedium>
            </div>

            {!isCartEmpty && cart._calculated.tax > 0 && (
                <div className="flex justify-between mb-1rem">
                    <BodySmall>Tax</BodySmall>
                    <BodySmall>{formatMoney(cart._calculated.tax, cart.locale)}</BodySmall>
                </div>
            )}

            {!isCartEmpty && cart.member_credit > 0 && (
                <div className="flex justify-between mb-0.5rem" style={{ color: '#E34E3D' }}>
                    <BodySmall>Store Credit Auto Applied</BodySmall>
                    <BodySmall>-{formatMoney(cart.member_credit, cart.locale)}</BodySmall>
                </div>
            )}

            {cart.available_member_credit + cart._calculated.appliedStoreCredit > 0 && !isCartEmpty && (
                <div className="flex justify-between mb-0.5rem">
                    <BodySmall>
                        Available Store Credit{' '}
                        <TooltipInfo text="This credit can only be used by the logged in account holder" />
                    </BodySmall>
                    <BodySmall>{formatMoney(cart.available_member_credit, cart.locale)}</BodySmall>
                </div>
            )}

            {!isCartEmpty &&
                cart._calculated.discounts.map((discount, index) => {
                    let label = discount.promotional_discount_description
                        ? discount.promotional_discount_description
                        : `Promo Code: ${discount.promotional_code}`;

                    if (discount.type === 'event_credit') {
                        label = 'Event Credit';
                    }

                    return (
                        <CartSummaryDiscountDisplay
                            key={`${discount.promotional_code}-${index}`}
                            amount={discount.promotional_discount}
                            label={label}
                            locale={cart.locale}
                        />
                    );
                })}

            <DividerLightGreyStyled className="mb-1rem" />

            {!hideDiscountsSection && (
                <>
                    <CartSummaryDiscount setCartHasErrors={setCartHasErrors} />
                    <DividerLightGreyStyled className="mb-1rem mt-1rem" />
                </>
            )}

            <div className="flex justify-between">
                <H4>Total</H4>
                <H4>{formatMoney(cart.total, cart.locale)}</H4>
            </div>

            {cart.items.length > 0 && (
                <>
                    <DividerLightGreyStyled className="mb-1rem mt-1rem" />
                    <CartSummaryItems />
                </>
            )}
        </div>
    );
};

CartSummary.propTypes = {
    hideDiscountsSection: PropTypes.bool,
    setCartHasErrors: PropTypes.func,
};

export default CartSummary;
